import type { ProductId } from "./registry";

/**
 * What this console covers, surface by surface, per product.
 *
 * ONE PLACE, deliberately. The alternative — each page describing its own gap —
 * is how a two-product console ends up with four differently-worded excuses for
 * the same missing API, and no way to answer "what is left?" without opening
 * every tab. This table answers it, and the flag surface renders it.
 *
 * It is a STATEMENT, not a probe. Whether `/mcp/admin/quota` answers right now
 * is the health check's question; whether this console draws a Labeeb envelope
 * is a fact about what has been built, and no request can discover it.
 *
 * `absent` never means broken. Every row marked absent is a surface the design
 * draws and the platform cannot serve — drawn blurred in the product so its
 * shape is visible and none of its figures are.
 */
export interface Coverage {
  /** The surface, named as the operator would say it. */
  surface: string;
  /** Where it lives in the console. */
  to: string;
  product: ProductId;
  status: "live" | "absent";
  /** The route behind it when live, or what it waits on when absent. */
  by: string;
}

export const COVERAGE: Coverage[] = [
  // ---------------------------------------------------------------- MCP --
  { surface: "Tenant list", to: "/tenants", product: "mcp", status: "live", by: "GET /mcp/admin/tenants" },
  { surface: "Tenant record — tier, caps, blocklist", to: "/tenants", product: "mcp", status: "live", by: "GET /mcp/admin/tenant/{ref}" },
  { surface: "Set a tenant's tier", to: "/tenants", product: "mcp", status: "live", by: "POST /mcp/admin/tenant/{ref}" },
  { surface: "Tier history", to: "/tenants", product: "mcp", status: "live", by: "GET /mcp/admin/tenant/{ref}/history" },
  { surface: "Usage — today", to: "/tenants", product: "mcp", status: "live", by: "GET /mcp/admin/tenant/{ref}/usage" },
  { surface: "Per-user blocks", to: "/tenants/access", product: "mcp", status: "live", by: "GET/POST /mcp/admin/tenant/{ref}/user/{email}" },
  { surface: "Merchant-wide blocks", to: "/tenants/access", product: "mcp", status: "live", by: "the blocklist rows on the tenant routes" },
  { surface: "Tool caps — read and write", to: "/policy/caps", product: "mcp", status: "live", by: "GET/PATCH /mcp/admin/quota, DELETE /mcp/admin/quota/{tier}/{tool}" },
  { surface: "Tool catalog", to: "/policy/caps", product: "mcp", status: "live", by: "GET /mcp/admin/catalog" },
  { surface: 'Tier credits — the "*" row', to: "/policy/tiers", product: "mcp", status: "live", by: "GET /mcp/admin/quota, GET /mcp/admin/tiers" },
  { surface: "Runtime settings", to: "/policy/flags", product: "mcp", status: "live", by: "GET/PATCH /mcp/admin/settings, POST /mcp/admin/settings/apply" },
  { surface: "Audit log", to: "/governance/audit", product: "mcp", status: "live", by: "GET /mcp/admin/audit[/{domain}[/{target}]]" },
  { surface: "Fleet metrics", to: "/", product: "mcp", status: "live", by: "GET /mcp/admin/fleet/metrics" },
  // The MAP is live — it renders, and every level in it derives from the
  // token's groups. ENFORCEMENT is a separate thing and belongs to the API
  // gateway by design, so a role changes in one place for every product rather
  // than per backend. That check is not live yet, so this row says what the
  // console does and stops short of claiming the server does it too.
  { surface: "Permission map & health", to: "/governance/permissions", product: "mcp", status: "live", by: "the cognito:groups claim — the console's own check; gateway enforcement is not live yet" },
  { surface: "Tenant distribution by tier", to: "/policy/tiers", product: "mcp", status: "live", by: "GET /mcp/admin/tenants, counted by stored and by effective tier" },

  // MCP's own gaps. Not Labeeb's, and worth separating: these are routes the
  // MCP admin API could add without another product existing.
  { surface: "Usage over 30 days", to: "/tenants", product: "mcp", status: "absent", by: "a usage history route — the usage endpoint serves the current UTC day only" },
  { surface: "Computed cost, billed against actual", to: "/", product: "mcp", status: "absent", by: "a billing or warehouse surface — neither product reports cost" },

  // ------------------------------------------------------------- Labeeb --
  // Labeeb's admin API is now mounted on this gateway (dev), and Beta grants
  // below is the one surface with a complete backing endpoint — everything
  // else here is still absent because the SPECIFIC route it needs does not
  // exist on Labeeb's admin API yet, not because the product is unwired.
  { surface: "Tier envelope — messages, tokens, Snowflake", to: "/policy/tiers", product: "labeeb", status: "live", by: "GET /labeeb/admin/tiers" },
  { surface: "What each tier includes", to: "/policy/tiers", product: "labeeb", status: "absent", by: "nothing to read — Features is not editable and the runtime never reads it, so there is no live field behind this" },
  { surface: "A tenant's envelope and consumption", to: "/tenants", product: "labeeb", status: "absent", by: "a tenant route on Labeeb's admin API" },
  { surface: "A tenant's tier", to: "/tenants", product: "labeeb", status: "absent", by: "a tenant route on Labeeb's admin API" },
  { surface: "Suspend the assistant", to: "/tenants/access", product: "labeeb", status: "absent", by: "a suspension route on Labeeb's admin API" },
  { surface: "Effective agents — the resolver", to: "/tenants", product: "labeeb", status: "absent", by: "an agents route on Labeeb's admin API" },
  { surface: "stable_agents — set at deploy", to: "/tenants", product: "labeeb", status: "absent", by: "an agents route on Labeeb's admin API" },
  { surface: "Beta grants", to: "/policy/beta", product: "labeeb", status: "live", by: "GET /labeeb/admin/catalog, GET/PATCH /labeeb/admin/tenant/{id}" },
  { surface: "HITL gates", to: "/policy/hitl", product: "labeeb", status: "absent", by: "a gates route on Labeeb's admin API" },
  { surface: "Greeting & chips", to: "/policy/welcome", product: "labeeb", status: "absent", by: "a content route on Labeeb's admin API" },
  { surface: "Runtime flags", to: "/policy/flags", product: "labeeb", status: "live", by: "GET /labeeb/admin/flags, PATCH /labeeb/admin/flags/{flag}" },
  { surface: "Fleet evidence", to: "/tenants/evidence", product: "labeeb", status: "absent", by: "a fleet route on Labeeb's admin API" },
  { surface: "Messages and profiled users", to: "/", product: "labeeb", status: "absent", by: "a fleet route on Labeeb's admin API" },
  // Worth its own row rather than folding into the rest. Enforcement is NOT
  // each product's own job — it belongs at the API gateway, deliberately, so
  // that adding or changing a role applies across every product's routes
  // instead of being reimplemented per backend. Until that check ships, the
  // console's own gating is the only gating, for both products — which is
  // exactly the sort of thing an authority table must not quietly imply.
  { surface: "Permission map — enforcement", to: "/governance/permissions", product: "labeeb", status: "absent", by: "the gateway's group check, which covers every product at once and is not live yet" },
];

export function coverageFor(product: ProductId): Coverage[] {
  return COVERAGE.filter((c) => c.product === product);
}

export function countsFor(product: ProductId): { live: number; absent: number } {
  const rows = coverageFor(product);
  return {
    live: rows.filter((r) => r.status === "live").length,
    absent: rows.filter((r) => r.status === "absent").length,
  };
}
